import { CommonModule } from '@angular/common';
import {
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
    signal,
    ViewChild,
    WritableSignal
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { isPlatform } from '@ionic/angular';

import { Camera, CameraResultType } from '@capacitor/camera';

import { ImageCroppedEvent, ImageCropperComponent, LoadedImage } from 'ngx-image-cropper';

import { MobileAppService } from '../../services/mobile-app/mobile-app.service';
import { AvatarImageComponent } from '../avatar-image/avatar-image.component';
import { ButtonComponent, IconButtonComponent } from '../buttons';
import { PlusCircleIconComponent } from '../icons/plus-circle-icon/plus-circle-icon.component';
import { SvgIconComponent } from '../icons/svg-icon/svg-icon.component';

@Component({
    selector: 'lib-image-selector',
    standalone: true,
    imports: [
        CommonModule,
        ImageCropperComponent,
        IconButtonComponent,
        ButtonComponent,
        SvgIconComponent,
        AvatarImageComponent,
        PlusCircleIconComponent
    ],
    templateUrl: './image-selector.component.html',
    styleUrl: './image-selector.component.css'
})
export class ImageSelectorComponent implements OnInit {
    /* eslint-disable @typescript-eslint/no-explicit-any */
    /* eslint-disable @typescript-eslint/no-unused-vars */
    sanitizer = inject(DomSanitizer);
    mobileAppService = inject(MobileAppService);

    @Input() maxNumberOfImages: number = 1;
    @Input() imageDisplay: 'full' | 'thumbnail' | 'avatar' | 'none' | 'document' = 'thumbnail';
    @Input() output: 'blob' | 'base64' = 'base64';
    @Input() roundCropper: boolean = false;
    @Input() initialValue: any[] = [];
    @Input() setInitialsAsCurrentUser: boolean = false;
    @Input() uploadButtonType: 'initial-wide' | 'square' = 'square';
    @Input() uploadButtonLabel: string = 'Upload';

    @Input() showActionSheet: boolean = true;

    @Input() errorText: string | undefined;
    @Input() disabled: boolean = false;

    @Output() imagesForUpload = new EventEmitter();

    @ViewChild('uploader', { static: true }) uploader!: ElementRef;
    @ViewChild(ImageCropperComponent, { static: true }) imageCropper!: ImageCropperComponent;

    imageChangedEvent: Event | null = null;

    croppedImages: any[] = [];

    isLoadingImage: boolean = false;

    showAddButton: boolean = true;

    base64Image: WritableSignal<string | null> = signal(null);

    ngOnInit(): void {
        if (this.initialValue.length) {
            this.croppedImages = [...this.initialValue];
        }
    }

    toggleAddButton() {
        this.showAddButton = this.croppedImages.length < this.maxNumberOfImages;
    }

    openCropper() {
        if (this.disabled) return;
        this.toggleAddButton();
        this.uploader.nativeElement.click();
    }

    fileChangeEvent(event: any): void {
        this.imageChangedEvent = event;

        this.isLoadingImage = true;

        if (event.target && event.target.value !== '' && event.target.value !== null) {
            this.isLoadingImage = true;
        }

        this.errorText = undefined;
    }

    imageCropped(event: ImageCroppedEvent) {
        if (this.imageDisplay === 'avatar' && (event.objectUrl || event.base64) && this.croppedImages.length) {
            if (event.base64) {
                this.croppedImages[0] = event.base64;
            } else if (event.objectUrl) {
                this.croppedImages[0] = this.sanitizeImage(event.objectUrl);
            }
        } else {
            if (event.base64) {
                this.croppedImages.push(event.base64);
            } else if (event.objectUrl) {
                this.croppedImages.push(this.sanitizeImage(event.objectUrl));
            }
        }

        this.uploader.nativeElement.value = null;
        this.imageChangedEvent = null;

        console.log(this.croppedImages);
        this.imagesForUpload.emit(this.croppedImages);

        this.base64Image.set(null);
        this.toggleAddButton();
    }

    sanitizeImage(objectUrl: string) {
        return this.sanitizer.bypassSecurityTrustUrl(objectUrl);
    }

    imageLoaded(image: LoadedImage) {
        // show cropper
    }

    cropperReady() {
        // cropper ready
        this.isLoadingImage = false;
    }

    loadImageFailed() {
        // show message
        this.isLoadingImage = false;
    }

    removeImage(index: number) {
        this.croppedImages.splice(index, 1);
        this.imagesForUpload.emit(this.croppedImages);
        this.toggleAddButton();
    }

    async selectFile() {
        if (isPlatform('capacitor')) {
            const hasCameraPermission = this.mobileAppService.hasCameraPermission();

            if (!hasCameraPermission) {
                return;
            }

            this.toggleAddButton();

            const image = await Camera.getPhoto({
                quality: 80,
                resultType: CameraResultType.Base64,
                saveToGallery: true
            });

            if (image.base64String) {
                this.base64Image.set(image.base64String);
                this.imageCropper.crop('base64');
            }
        } else {
            this.openCropper();
        }
    }
}
